import { useEffect, useId, useMemo, useState } from "react";

import {
  Group,
  Input,
  MultiSelect,
  MultiSelectProps,
  Select,
  SelectProps,
  Stack,
} from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { TodoConstants } from "constant";

import { createMonthOptions } from "../helpers";

import classes from "./DateTimeSelect.module.css";

const { DAYS, YEARS } = TodoConstants;

// Custom Select with animated chevron
export const ChevronSelect = (props: SelectProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Select
      {...props}
      rightSection={isOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
      onDropdownOpen={() => setIsOpen(true)}
      onDropdownClose={() => setIsOpen(false)}
    />
  );
};

// Custom MultiSelect with animated chevron
export const ChevronMultiSelect = (props: MultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <MultiSelect
      {...props}
      rightSection={isOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
      onDropdownOpen={() => setIsOpen(true)}
      onDropdownClose={() => setIsOpen(false)}
    />
  );
};

interface DateTimeSelectProps {
  day: string | null;
  month: string | null;
  year: string | null;
  time: string;
  onDayChange: (value: string | null) => void;
  onMonthChange: (value: string | null) => void;
  onYearChange: (value: string | null) => void;
  onTimeChange: (value: string) => void;
  dayError?: boolean;
  monthError?: boolean;
  yearError?: boolean;
  timeError?: boolean;
  disabled?: boolean;
}

const DateTimeSelect = ({
  day,
  month,
  year,
  time,
  onDayChange,
  onMonthChange,
  onYearChange,
  onTimeChange,
  dayError,
  monthError,
  yearError,
  timeError,
  disabled,
}: DateTimeSelectProps) => {
  const { t } = useTranslation();
  const monthOptions = useMemo(() => createMonthOptions(t), [t]);
  const [invalidDay, setInvalidDay] = useState(false);
  const dayErrorId = useId();
  // Until a year is selected, allow February 29; the chosen year resolves it.
  const daysInMonth = month ? new Date(Number(year || 2000), Number(month), 0).getDate() : 31;

  useEffect(() => {
    if (day && Number(day) > daysInMonth) {
      setInvalidDay(true);
      onDayChange(null);
    } else if (day) {
      setInvalidDay(false);
    }
  }, [day, daysInMonth, onDayChange]);

  return (
    <Stack gap={4}>
      <Group gap="xs">
        <ChevronSelect
          data={DAYS.slice(0, daysInMonth)}
          placeholder="1"
          w={70}
          radius="md"
          size="sm"
          value={day}
          onChange={(value) => {
            setInvalidDay(false);
            onDayChange(value);
          }}
          error={invalidDay || dayError}
          aria-describedby={invalidDay ? dayErrorId : undefined}
          disabled={disabled}
        />
        <ChevronSelect
          data={monthOptions}
          placeholder={monthOptions[0]?.label}
          w={120}
          radius="md"
          size="sm"
          value={month}
          onChange={onMonthChange}
          error={monthError}
          disabled={disabled}
        />
        <ChevronSelect
          data={YEARS}
          placeholder={String(new Date().getFullYear())}
          w={80}
          radius="md"
          size="sm"
          value={year}
          onChange={onYearChange}
          error={yearError}
          disabled={disabled}
        />
        <TimeInput
          value={time}
          onChange={(e) => onTimeChange(e.target.value)}
          w={110}
          radius="md"
          size="sm"
          classNames={{ input: classes.timeInput }}
          error={timeError}
          disabled={disabled}
        />
      </Group>
      {invalidDay && <Input.Error id={dayErrorId}>{t("todoList.invalidDay")}</Input.Error>}
    </Stack>
  );
};

export default DateTimeSelect;
